/**
 * Pure-code treble staff for Phase 5 Western notation mode.
 */

import { isAccidental, staffStepsFromC4 } from "@piano-tutor/shared";

export class StaffCanvas {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private notes: number[] = [];
  private dpr = 1;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas 2D unavailable");
    this.ctx = ctx;
    this.resize();
    window.addEventListener("resize", () => this.resize());
  }

  setNotes(midiNotes: number[]): void {
    this.notes = [...midiNotes].sort((a, b) => a - b);
    this.draw();
  }

  resize(): void {
    const parent = this.canvas.parentElement;
    const cssW = parent?.clientWidth || 800;
    const shortLandscape =
      typeof window !== "undefined" &&
      window.matchMedia("(max-height: 560px) and (orientation: landscape)").matches;
    const tablet =
      typeof window !== "undefined" &&
      window.matchMedia("(min-width: 721px) and (max-width: 1180px)").matches;
    const cssH = shortLandscape ? 110 : tablet ? Math.round(Math.min(168, Math.max(128, window.innerHeight * 0.16))) : 140;
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = Math.floor(cssW * this.dpr);
    this.canvas.height = Math.floor(cssH * this.dpr);
    this.canvas.style.width = `${cssW}px`;
    this.canvas.style.height = `${cssH}px`;
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    this.draw();
  }

  draw(): void {
    const cssW = this.canvas.width / this.dpr;
    const cssH = this.canvas.height / this.dpr;
    const ctx = this.ctx;
    ctx.clearRect(0, 0, cssW, cssH);

    const top = 28;
    const spacing = 12;
    // 5 staff lines
    ctx.strokeStyle = "rgba(26,22,18,0.55)";
    ctx.lineWidth = 1.25;
    for (let i = 0; i < 5; i++) {
      const y = top + i * spacing;
      ctx.beginPath();
      ctx.moveTo(48, y);
      ctx.lineTo(cssW - 12, y);
      ctx.stroke();
    }

    // Treble clef (simplified glyph as text)
    ctx.fillStyle = "rgba(26,22,18,0.85)";
    ctx.font = "64px 'Cormorant Garamond', Georgia, serif";
    ctx.fillText("𝄞", 8, top + spacing * 4.1);

    // Middle C is one ledger below the staff bottom (step -2 from bottom line degree math)
    // Staff bottom line (E4) = step +2 from C4 in diatonic steps.
    // Position: step 2 → bottom line. y = top + (4 - (step - 2)) * (spacing/2) ...
    const yForStep = (step: number) => {
      // step 2 (E4) = bottom line = top + 4*spacing
      // each diatonic step = spacing/2
      return top + 4 * spacing - (step - 2) * (spacing / 2);
    };

    const unique = [...new Set(this.notes)];
    unique.forEach((midi, i) => {
      const step = staffStepsFromC4(midi);
      const x = 100 + i * 36;
      const y = yForStep(step);

      // ledger lines for below/above
      ctx.strokeStyle = "rgba(26,22,18,0.5)";
      ctx.lineWidth = 1;
      if (step < 2) {
        for (let s = 0; s >= step; s -= 2) {
          if (s === 2) continue;
          const ly = yForStep(s);
          if (s % 2 === 0 && s < 2) {
            ctx.beginPath();
            ctx.moveTo(x - 14, ly);
            ctx.lineTo(x + 14, ly);
            ctx.stroke();
          }
        }
        // ensure middle C ledger
        if (step <= 0) {
          const ly = yForStep(0);
          ctx.beginPath();
          ctx.moveTo(x - 14, ly);
          ctx.lineTo(x + 14, ly);
          ctx.stroke();
        }
      }
      if (step > 10) {
        for (let s = 12; s <= step; s += 2) {
          const ly = yForStep(s);
          ctx.beginPath();
          ctx.moveTo(x - 14, ly);
          ctx.lineTo(x + 14, ly);
          ctx.stroke();
        }
      }

      // note head
      ctx.fillStyle = "#1a1612";
      ctx.beginPath();
      ctx.ellipse(x, y, 7, 5.5, -0.3, 0, Math.PI * 2);
      ctx.fill();

      if (isAccidental(midi)) {
        ctx.font = "16px 'IBM Plex Sans', sans-serif";
        ctx.fillText("♯", x - 22, y + 5);
      }
    });

    if (unique.length === 0) {
      ctx.fillStyle = "rgba(26,22,18,0.45)";
      ctx.font = "14px 'IBM Plex Sans', sans-serif";
      ctx.fillText("Play notes or load a piece — staff updates live.", 100, top + spacing * 2.2);
    }
  }
}
